/**
 * Sets up fallback focus candidates for when the dialog opener is removed from the DOM.
 * Tracks parent order/item elements and their siblings as potential focus targets.
 */
export const setFocusFallbackRegion = (opener: HTMLElement | null): Element[] => {
  if (!opener) return [];

  // we need to track the next/previous orders and items in case interacting with this dialog removes the opener from the DOM
  const parentOrder = opener.closest<HTMLElement>('[data-order-id]');
  const parentItem = opener.closest<HTMLElement>('[data-order-item-id]');
  const nextOrderToFocus = [parentOrder?.nextElementSibling, parentOrder?.previousElementSibling].filter(
    el => el?.matches('[data-order-id]')
  )[0];
  const nextItemToFocus = [parentItem?.nextElementSibling, parentItem?.previousElementSibling].filter(
    el => el?.matches('[data-order-item-id]')
  )[0];

  // candidates for where we could move focus if focus is lost when the dialog is closed
  // prefer the current item/order, or the next item/order if the current item/order is no longer present
  return [parentItem, nextItemToFocus, parentOrder, nextOrderToFocus].filter((el): el is Element => el != null);
};

/**
 * Manages focus restoration when the cancel-flow dialog closes. Adapted from similar logic in smi-templates.
 * Handles three cases:
 * 1. Opener disconnected and focus lost - focuses first connected fallback region heading
 * 2. Opener inside dropdown - focuses the dropdown trigger instead
 * 3. Normal case - focuses the opener (handled by native dialog)
 */
export const manageFocus = (openerElement: HTMLElement | null, focusRegionCandidates: Element[]): void => {
  if (!openerElement) return;

  let toFocus: HTMLElement | null = null;
  const dropdown = openerElement.closest<HTMLElement>('sm-dropdown');

  // sometimes the element we return focus to is no longer in the DOM
  // only run this if document.activeElement is the body (which happens when focus is lost) to allow other manual focus management
  if (!openerElement.isConnected && focusRegionCandidates && document.activeElement === document.body) {
    const focusRegion = focusRegionCandidates.filter(el => el.isConnected)[0];
    toFocus = focusRegion?.querySelector('h3, h4');
    if (toFocus) {
      // make it so we can focus the heading
      toFocus.setAttribute('tabindex', '-1');
    }
  } else if (dropdown) {
    // normally the native <dialog> returns focus to the trigger element for us
    // however, if the element that opened the dialog was inside a dropdown, the dropdown is now closed and the element inside cannot be focused
    // detect this case and manually focus the trigger instead
    toFocus = dropdown;
  }

  toFocus?.focus();
};
